import { apiService } from './api';
import { LoginCredentials, AuthResponse, User, UserCreateResponse, ChangePasswordData } from '../types';

export class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiService.post<{ access_token: string; token_type: string; must_change_password?: boolean }>('/api/v1/auth/login', credentials);

      // Store token in localStorage
      localStorage.setItem('access_token', response.access_token);

      // Get user data after login - handle failure gracefully
      let userData: User | null = null;
      try {
        userData = await this.getCurrentUser();
      } catch (userError) {
        console.warn('Failed to fetch user data after login, but login was successful:', userError);
        // Login was successful, user data can be fetched later
      }

      return {
        access_token: response.access_token,
        token_type: response.token_type,
        refresh_token: '', // Backend uses HTTP-only cookies for refresh tokens
        user: userData,
        must_change_password: response.must_change_password
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await apiService.post('/api/v1/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem('access_token');
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      return await apiService.get<User>('/api/v1/auth/me');
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  // Admin user management methods
  async getUsers(): Promise<User[]> {
    try {
      return await apiService.get<User[]>('/api/v1/users/');
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  }

  async createUser(userData: { username: string; is_superuser?: boolean }): Promise<UserCreateResponse> {
    try {
      // Password will be auto-generated by the backend
      return await apiService.post<UserCreateResponse>('/api/v1/auth/signup', userData);
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  }

  async changePassword(data: ChangePasswordData): Promise<void> {
    try {
      await apiService.post('/api/v1/users/me/change-password', data);
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  async updateUser(userId: string, userData: { username?: string; password?: string; is_superuser?: boolean }): Promise<User> {
    try {
      return await apiService.put<User>(`/api/v1/users/${userId}`, userData);
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      await apiService.delete(`/api/v1/users/${userId}`);
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  }
}

export const authService = new AuthService();
